// [PURPOSE]: Hook to check premium subscription status and manage feature gating
// [WHY]: Components need to know if user has premium access for conditional rendering
// [INTEGRATION]: Uses AuthContext for premium state, provides convenience methods
// [CREATED]: v1.0 Track 2A — Premium status hook

import { useAuth } from '../contexts/AuthContext';

/**
 * [PURPOSE]: Convenience hook for premium feature gating
 * [WHY]: Simplifies checking premium status + token balance in components
 *
 * @returns {Object} Premium status and helper methods
 *
 * Usage:
 *   const { isPremium, canGenerate, tokensRemaining, requirePremium } = useSubscription();
 *   if (!requirePremium('Content Workshop')) return <PremiumGate />;
 */
export const useSubscription = () => {
  const {
    isAuthenticated,
    isPremium,
    aiTokens,
    currentUser,
    premiumStatus,
    refreshPremiumStatus,
  } = useAuth();

  /**
   * [PURPOSE]: Check if user can generate AI images (has tokens)
   * @param {number} cost - Number of tokens needed (1 for icon, 3 for portrait)
   * @returns {boolean} True if user has enough tokens (premium tier no longer required)
   */
  const canGenerate = (cost = 1) => {
    return aiTokens >= cost;
  };

  /**
   * [PURPOSE]: Check if a premium feature should be accessible
   * @param {string} featureName - Name of the feature for logging
   * @returns {boolean} True if user has premium access
   */
  const requirePremium = (featureName) => {
    if (!isAuthenticated) return false;
    if (!isPremium) return false;
    return true;
  };

  /**
   * [PURPOSE]: Get the number of remaining AI tokens
   * @returns {number} Token balance
   */
  const tokensRemaining = aiTokens || 0;

  /**
   * [PURPOSE]: Check character limit
   * @param {number} currentCount - Number of existing characters
   * @returns {boolean} True if user can create more characters
   */
  const canCreateCharacter = (currentCount = 0) => {
    // All users: unlimited characters
    return true;
  };

  /**
   * [PURPOSE]: Get feature access map for UI rendering
   * [WHY]: Components can check multiple features at once
   * @returns {Object} Feature access map
   */
  const featureAccess = {
    // Always available (free tier)
    characterSheet: true,
    basicInventory: true,
    spellManagement: true,
    localSave: true,

    // Ungated — all users get these features
    unlimitedCharacters: true,
    contentWorkshop: true,
    shopGenerator: true,
    bastionSystem: true,
    cloudBackup: true,
    aiGeneration: aiTokens > 0,   // Token-balance only; server enforces too

    // KEEP gated — real-time party sync is the only true premium feature
    partySync: isPremium,
  };

  return {
    // State
    isAuthenticated,
    isPremium,
    aiTokens: tokensRemaining,
    currentUser,
    premiumStatus,

    // Methods
    canGenerate,
    requirePremium,
    canCreateCharacter,
    refreshPremiumStatus,

    // Feature map
    featureAccess,
  };
};

export default useSubscription;
